import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import {
  uploadFile,
  FileUploadResponse,
} from '@/services/workflow/upload-file';
import { useNodeStore } from '@/stores/node-store';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';

export type UploadPhase = 'uploading' | 'processing' | 'done' | 'failed';

export interface UploadItem {
  uploadId: string;
  nodeId: string;
  fieldId: string;
  workflowId: string;
  nodeName?: string;
  filename: string;
  size: number;
  loaded: number;
  percent: number;
  phase: UploadPhase;
  error?: string;
}

const DONE_TTL_MS = 4000;

const uploadIdOf = (nodeId: string, fieldId: string) => `${nodeId}::${fieldId}`;

// AbortControllers and the source File are held outside the reactive store:
// neither is serializable, and they only need to survive until the upload
// settles. Keyed by uploadId, which is unique per node field.
const controllers = new Map<string, AbortController>();
const removalTimers = new Map<string, ReturnType<typeof setTimeout>>();

// Every field reuses one uploadId, so a re-upload/cancel-then-repick starts a
// fresh attempt under the same key. Each attempt gets a monotonic generation;
// callbacks from a superseded attempt check it and bail before mutating shared
// state, otherwise the old promise would corrupt the new upload.
const generations = new Map<string, number>();
let generationCounter = 0;

interface StartUploadParams {
  nodeId: string;
  fieldId: string;
  workflowId: string;
  file: File;
  nodeName?: string;
}

interface UploadStore {
  uploads: Record<string, UploadItem>;
  startUpload: (params: StartUploadParams) => string;
  cancelUpload: (uploadId: string) => void;
  removeUpload: (uploadId: string) => void;
  dismissResolved: () => void;
}

const setItem = (
  set: (fn: (state: UploadStore) => Partial<UploadStore>) => void,
  uploadId: string,
  patch: Partial<UploadItem>,
) =>
  set((state) => {
    const existing = state.uploads[uploadId];
    if (!existing) return {};
    return {
      uploads: { ...state.uploads, [uploadId]: { ...existing, ...patch } },
    };
  });

const clearRemovalTimer = (uploadId: string) => {
  const timer = removalTimers.get(uploadId);
  if (timer) {
    clearTimeout(timer);
    removalTimers.delete(uploadId);
  }
};

export const useUploadStore = create<UploadStore>((set, get) => {
  const run = (params: StartUploadParams): string => {
    const { nodeId, fieldId, workflowId, file, nodeName } = params;
    const uploadId = uploadIdOf(nodeId, fieldId);

    clearRemovalTimer(uploadId);
    controllers.get(uploadId)?.abort();

    const generation = ++generationCounter;
    generations.set(uploadId, generation);
    const isStale = () => generations.get(uploadId) !== generation;

    const controller = new AbortController();
    controllers.set(uploadId, controller);

    set((state) => ({
      uploads: {
        ...state.uploads,
        [uploadId]: {
          uploadId,
          nodeId,
          fieldId,
          workflowId,
          nodeName,
          filename: file.name,
          size: file.size,
          loaded: 0,
          percent: 0,
          phase: 'uploading',
          error: undefined,
        },
      },
    }));

    uploadFile(file, workflowId, nodeId, {
      signal: controller.signal,
      onProgress: ({ loaded, total }) => {
        if (isStale()) return;
        const percent =
          total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
        setItem(set, uploadId, {
          loaded,
          percent,
          // Bytes are on the wire until loaded === total; after that the
          // request is waiting on the server, which the UI shows as Processing.
          phase: loaded >= total && total > 0 ? 'processing' : 'uploading',
        });
      },
    })
      .then((response: FileUploadResponse) => {
        if (isStale()) return;
        if (response.success && response.file) {
          useNodeStore
            .getState()
            .handleParameterChange(nodeId, fieldId, response);
          useWorkflowValidationStore
            .getState()
            .clearMissingFile(nodeId, fieldId);
          setItem(set, uploadId, { phase: 'done', percent: 100 });
          const timer = setTimeout(
            () => get().removeUpload(uploadId),
            DONE_TTL_MS,
          );
          removalTimers.set(uploadId, timer);
        } else {
          setItem(set, uploadId, {
            phase: 'failed',
            error: response.message || 'Upload failed',
          });
        }
      })
      .catch((error: unknown) => {
        if (isStale()) return;
        if (error instanceof DOMException && error.name === 'AbortError') {
          // Cancel returns the field straight to its empty upload control on
          // every surface, so the user can immediately pick another file.
          get().removeUpload(uploadId);
          return;
        }
        const message =
          error instanceof Error ? error.message : 'Unknown error occurred';
        Sentry.captureException(error, {
          extra: {
            context: 'use-upload-store:startUpload',
            nodeId,
            fieldId,
            workflowId,
          },
        });
        setItem(set, uploadId, { phase: 'failed', error: message });
      })
      .finally(() => {
        // A newer attempt may already own this uploadId (and its controller);
        // only the attempt that is still current may clean up.
        if (isStale()) return;
        controllers.delete(uploadId);
      });

    return uploadId;
  };

  return {
    uploads: {},

    startUpload: run,

    cancelUpload: (uploadId) => {
      controllers.get(uploadId)?.abort();
    },

    removeUpload: (uploadId) => {
      clearRemovalTimer(uploadId);
      controllers.delete(uploadId);
      generations.delete(uploadId);
      set((state) => {
        if (!state.uploads[uploadId]) return {};
        const next = { ...state.uploads };
        delete next[uploadId];
        return { uploads: next };
      });
    },

    dismissResolved: () => {
      set((state) => {
        const next: Record<string, UploadItem> = {};
        Object.values(state.uploads).forEach((item) => {
          if (item.phase === 'uploading' || item.phase === 'processing') {
            next[item.uploadId] = item;
          } else {
            clearRemovalTimer(item.uploadId);
            generations.delete(item.uploadId);
          }
        });
        return { uploads: next };
      });
    },
  };
});

export const isActivePhase = (phase: UploadPhase) =>
  phase === 'uploading' || phase === 'processing';

export const useNodeUpload = (
  nodeId: string,
  fieldId: string,
): UploadItem | undefined =>
  useUploadStore((state) => state.uploads[uploadIdOf(nodeId, fieldId)]);
